import { useState, useEffect } from 'react';
import axios from 'axios';
import { authAxios } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2, Search, X, Tag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import Pagination from '../../components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProduct = {
  name: '',
  description: '',
  price: '',
  discount_price: '',
  category_id: '',
  image: '',
  images: [],
  stock: '',
  unit: 'piece',
  is_active: true,
  is_returnable: true,
  deliverable_pincodes: ['247776'],
  display_order: 0,
  hsn_code: '',       // HSN code string e.g. "1006"
  hsn_id: '',         // reference to the hsn entry id
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);   // loaded from settings
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchHsnCodes();
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get(`${API}/products/all?page=${currentPage}&page_size=20`);
      const data = response.data;
      setProducts(data.products || data);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await authAxios.get(`${API}/categories/all`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchHsnCodes = async () => {
    try {
      const response = await authAxios.get(`${API}/admin/settings`);
      setHsnCodes(response.data.hsn_codes || []);
    } catch (error) {
      console.error('Failed to fetch HSN codes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Resolve HSN code string from selected hsn_id
    const selectedHsn = hsnCodes.find(h => h.id === formData.hsn_id);

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
      stock: parseInt(formData.stock),
      hsn_code: selectedHsn?.code || formData.hsn_code || '',
      hsn_description: selectedHsn?.description || '',
      hsn_gst_rate: selectedHsn?.gst_rate ?? null,
    };

    try {
      if (editingProduct) {
        await authAxios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await authAxios.post(`${API}/products`, payload);
        toast.success('Product added');
      }
      setDialogOpen(false);
      setEditingProduct(null);
      setFormData(emptyProduct);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setDialogOpen(false);
    setEditingProduct(product);
    // Try to match hsn_code back to an hsn_id from settings
    const matchedHsn = hsnCodes.find(h => h.code === product.hsn_code);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      discount_price: product.discount_price?.toString() || '',
      category_id: product.category_id,
      image: product.image,
      images: product.images || [],
      stock: product.stock.toString(),
      unit: product.unit,
      is_active: product.is_active,
      is_returnable: product.is_returnable !== undefined ? product.is_returnable : true,
      deliverable_pincodes: product.deliverable_pincodes || ['247776'],
      display_order: product.display_order || 0,
      hsn_code: product.hsn_code || '',
      hsn_id: matchedHsn?.id || '',
    });
    setTimeout(() => setDialogOpen(true), 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await authAxios.delete(`${API}/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await authAxios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image: response.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const uploadedUrls = [];
    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await authAxios.post(`${API}/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(response.data.url);
      }
      setFormData({ ...formData, images: [...formData.images, ...uploadedUrls] });
      toast.success(`${uploadedUrls.length} images uploaded`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  // When HSN is selected from dropdown, store the id; clear if "none"
  const handleHsnSelect = (value) => {
    if (value === 'none') {
      setFormData({ ...formData, hsn_id: '', hsn_code: '' });
    } else {
      const selected = hsnCodes.find(h => h.id === value);
      setFormData({ ...formData, hsn_id: value, hsn_code: selected?.code || '' });
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatPrice = (price) => `₹${price}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Products</h1>

        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) { setEditingProduct(null); setFormData(emptyProduct); }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Product Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter product name"
                    required
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter product description"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price (₹)</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Discount Price (₹)</Label>
                    <Input
                      type="number"
                      value={formData.discount_price}
                      onChange={(e) => setFormData({ ...formData, discount_price: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select
                      value={formData.unit}
                      onValueChange={(v) => setFormData({ ...formData, unit: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="piece">Piece</SelectItem>
                        <SelectItem value="kg">Kg</SelectItem>
                        <SelectItem value="g">Gram</SelectItem>
                        <SelectItem value="l">Litre</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                        <SelectItem value="pack">Pack</SelectItem>
                        <SelectItem value="dozen">Dozen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* ── HSN Code ── */}
                <div>
                  <Label className="flex items-center gap-1">
                    <Tag className="w-3.5 h-3.5 text-slate-500" />
                    HSN Code
                  </Label>
                  {hsnCodes.length === 0 ? (
                    <p className="text-xs text-amber-600 mt-1 p-2 bg-amber-50 rounded border border-amber-200">
                      No HSN codes found. Add them in <strong>App Settings → HSN Code Management</strong> first.
                    </p>
                  ) : (
                    <Select
                      value={formData.hsn_id || 'none'}
                      onValueChange={handleHsnSelect}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select HSN code (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {hsnCodes.map((hsn) => (
                          <SelectItem key={hsn.id} value={hsn.id}>
                            <span className="font-mono font-semibold">{hsn.code}</span>
                            <span className="text-slate-500 ml-2">— {hsn.description}</span>
                            <span className="text-blue-600 ml-2">({hsn.gst_rate}% GST)</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {formData.hsn_id && (() => {
                    const sel = hsnCodes.find(h => h.id === formData.hsn_id);
                    return sel ? (
                      <p className="text-xs text-slate-500 mt-1">
                        HSN <strong>{sel.code}</strong> · {sel.description} · GST <strong>{sel.gst_rate}%</strong>
                      </p>
                    ) : null;
                  })()}
                </div>

                <div>
                  <Label>Stock Quantity</Label>
                  <Input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="0"
                    required
                  />
                </div>

		<div>
		   <Label>Display Order</Label>
		   <Input
		      type="number"
		      value={formData.display_order}
		      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
    		      placeholder="0"
		      min="0"
		   />
		   <p className="text-xs text-slate-400 mt-1">Lower number = appears first. Default is 0.</p>
		</div>

                {/* Main Image */}
                <div>
                  <Label>Main Image</Label>
                  <div className="mt-1 flex items-center gap-4">
                    {formData.image && (
                      <div className="relative">
                        <img src={formData.image} alt="Preview" className="w-20 h-20 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, image: '' })}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <Input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                  </div>
                </div>

                {/* Additional Images */}
                <div>
                  <Label>Additional Images</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img} alt={`Preview ${idx}`} className="w-16 h-16 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <Input type="file" accept="image/*" multiple onChange={handleMultipleImagesUpload} disabled={uploading} className="w-auto" />
                  </div>
                </div>

                {/* Toggles */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} />
                    <Label>Active</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.is_returnable} onCheckedChange={(v) => setFormData({ ...formData, is_returnable: v })} />
                    <Label>Returnable</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={uploading}>
                    {uploading ? 'Uploading...' : (editingProduct ? 'Update' : 'Add Product')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Product</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">HSN</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Price</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Stock</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProducts.map((product) => {
                const category = categories.find(c => c.id === product.category_id);
                return (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={product.image} alt={product.name} className="w-12 h-12 object-cover rounded" />
                        <div>
                          <p className="font-medium text-slate-900">{product.name}</p>
                          <p className="text-xs text-slate-500">{product.unit}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{category?.name || '-'}</td>
                    <td className="px-4 py-3">
                      {product.hsn_code ? (
                        <div>
                          <span className="inline-block font-mono text-xs font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                            {product.hsn_code}
                          </span>
                          {product.hsn_gst_rate != null && (
                            <span className="block text-xs text-blue-600 mt-0.5">{product.hsn_gst_rate}% GST</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-medium text-green-600">
                          {formatPrice(product.discount_price || product.price)}
                        </span>
                        {product.discount_price && (
                          <span className="ml-2 text-xs text-slate-400 line-through">{formatPrice(product.price)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={product.stock > 0 ? 'default' : 'destructive'}>{product.stock}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={product.is_active ? 'default' : 'secondary'}>
                        {product.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(product)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredProducts.length === 0 && (
          <div className="p-8 text-center text-slate-500">No products found</div>
        )}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
}
